// /parkconnectadmin — the human in the middle of ParkConnect.
//
// Nothing on the public board got there without passing through this page. It
// shows the two queues: listings waiting on approval (with the photo ID and
// permit receipt the holder attached, which exist nowhere else in the UI), and
// parker signups waiting on an introduction. Both sides upload a photo ID, so
// this page is the only place either document can be looked at.
//
// A signup that names a listing is a **pairing**: the row puts the parker and
// the permit holder side by side — both sets of contact details, the vehicle
// that will sit in the stall, and whether their weeks actually overlap — so the
// approval is one read rather than two lookups.
//
// Login is the shared APP_PASSWORD via /api/auth — the same gate as /admin. The
// token lives in sessionStorage so a refresh keeps you in until it expires
// (~6h) or the tab closes.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSS } from './parkconnect/styles';
import { API_BASE, fetchAdminData, login, post } from './parkconnect/api';
import { storedDocUrl } from './utils/safeUrl';
import { dayShort, shortSummary, summarize } from './parkconnect/schedule';
import { CloseIcon, DocIcon, MarkIcon, SpinnerIcon } from './parkconnect/Icons';

const TOKEN_KEY = 'umaps-parkconnect-token';

function fmtTime(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function Cell({ k, children }) {
  return <div><div className="pc-k">{k}</div><div className="pc-v">{children}</div></div>;
}

const ID_LABEL = { student: 'Student ID', licence: "Driver's licence" };
const PAY_LABEL = { monthly: 'paid monthly', term: 'paid at start of term' };
const payLabel = (row) => PAY_LABEL[row.paymentPlan === 'term' ? 'term' : 'monthly'];

/** The photo ID cell, identical on both queues. */
function PhotoIdCell({ row, onDoc }) {
  const label = ID_LABEL[row.photoIdKind] || ID_LABEL.student;
  return (
    <Cell k="Photo ID">
      {row.photoId
        ? <button type="button" className="pc-btn tiny" onClick={() => onDoc(row.id, 'photoId', label)}>View {label.toLowerCase()}</button>
        : <span style={{ color: '#8a5a00' }}>None attached</span>}
    </Cell>
  );
}

function Hours({ availability, empty = 'No days set' }) {
  const groups = summarize(availability);
  if (!groups.length) return <span className="pc-small">{empty}</span>;
  return (
    <p className="pc-hours" style={{ marginTop: 0 }}>
      {groups.map((g, i) => (
        <span key={`${g.days}-${g.window}`}>{i > 0 && <br />}<b>{g.days}</b> — {g.window}</span>
      ))}
    </p>
  );
}

/** The weekdays a parker needs that the listing actually covers. */
function overlapOf(request, listing) {
  if (!listing) return null;
  const holderDays = new Set((listing.availability || []).map(s => s.day));
  const wanted = (request.availability || []).map(s => s.day);
  const both = wanted.filter(d => holderDays.has(d));
  return { wanted: wanted.length, both, missing: wanted.filter(d => !holderDays.has(d)) };
}

/**
 * The document viewer — photo IDs and permit receipts. A Blob upload opens in a
 * new tab; a dev-machine upload comes back inline as a data URL, because those
 * files live outside public/ on purpose and are never served statically.
 */
function DocModal({ doc, onClose }) {
  const isPdf = doc.mime === 'application/pdf' || /\.pdf$/i.test(doc.name || '');
  return (
    <div className="pc-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pc-modal wide" role="dialog" aria-modal="true" aria-label={doc.name || 'Document'}>
        <div className="pc-modal-head">
          <DocIcon size={16} />
          <strong style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.label}</strong>
          <button type="button" className="pc-modal-x" onClick={onClose} aria-label="Close"><CloseIcon size={15} /></button>
        </div>
        <div className="pc-modal-body">
          {isPdf
            ? <iframe className="pc-doc-frame" src={doc.src} title={doc.name || 'Document'} />
            : <img className="pc-doc-img" src={doc.src} alt={doc.name || 'Submitted document'} />}
          <div style={{ display: 'flex', gap: 7, marginTop: 12 }}>
            <a className="pc-btn sm" href={doc.src} target="_blank" rel="noopener noreferrer">Open in a new tab</a>
            <a className="pc-btn sm" href={doc.src} download={doc.name || 'document'}>Download</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ListingRow({ row, busy, onDecide, onDelete, onDoc }) {
  return (
    <div className="pc-panel">
      <div className="pc-row-top">
        <strong style={{ fontSize: 15 }}>Lot {row.lot}</strong>
        <span className={`pc-status ${row.status}`}>{row.status}</span>
        <span className="pc-spacer" />
        <strong style={{ fontSize: 15 }}>${row.price}</strong>
        <span className="pc-small">per month, {payLabel(row)}</span>
      </div>
      <p className="pc-small" style={{ margin: '4px 0 0' }}>
        {row.name} · submitted {fmtTime(row.ts)}
      </p>

      <div style={{ marginTop: 10 }}>
        <div className="pc-k">Free hours</div>
        <Hours availability={row.availability} />
      </div>

      <div className="pc-kv">
        <Cell k="Email"><a href={`mailto:${row.email}`}>{row.email}</a></Cell>
        <Cell k="Phone"><a href={`tel:${row.phone.replace(/[^\d+]/g, '')}`}>{row.phone}</a></Cell>
        <PhotoIdCell row={row} onDoc={onDoc} />
        <Cell k="Permit receipt">
          {row.permitReceipt
            ? <button type="button" className="pc-btn tiny" onClick={() => onDoc(row.id, 'receipt', 'Permit receipt')}>View</button>
            : <span style={{ color: '#8a5a00' }}>None attached</span>}
        </Cell>
        {row.scheduleNote && <Cell k="Schedule notes">{row.scheduleNote}</Cell>}
        {row.note && <Cell k="Public note">{row.note}</Cell>}
        {row.decidedTs && <Cell k="Decided">{fmtTime(row.decidedTs)}</Cell>}
      </div>

      <div className="pc-actions">
        {row.status !== 'approved' && (
          <button type="button" className="pc-btn sm ok" disabled={busy} onClick={() => onDecide(row.id, 'approved')}>
            {busy ? <SpinnerIcon size={13} /> : null} Approve and publish
          </button>
        )}
        {row.status !== 'rejected' && (
          <button type="button" className="pc-btn sm no" disabled={busy} onClick={() => onDecide(row.id, 'rejected')}>Reject</button>
        )}
        {row.status === 'approved' && (
          <button type="button" className="pc-btn sm" disabled={busy} onClick={() => onDecide(row.id, 'pending')}>Pull off the board</button>
        )}
        <span className="pc-spacer" />
        <button type="button" className="pc-btn sm" disabled={busy} onClick={() => onDelete('listings', row.id)}>Delete</button>
      </div>
    </div>
  );
}

function RequestRow({ row, listings, busy, onStatus, onMatch, onDelete, onDoc }) {
  const listing = listings.find(l => l.id === (row.matchedListingId || row.listingId));
  const approved = listings.filter(l => l.status === 'approved');
  const overlap = overlapOf(row, listing);

  return (
    <div className="pc-panel">
      <div className="pc-row-top">
        <strong style={{ fontSize: 15 }}>{row.name}</strong>
        <span className={`pc-status ${row.status}`}>{row.status}</span>
        <span className="pc-spacer" />
        <span className="pc-small">{fmtTime(row.ts)}</span>
      </div>
      <p className="pc-small" style={{ margin: '4px 0 0' }}>
        {listing ? `Wants the spot in lot ${listing.lot}` : 'Signed up — no specific spot'}
      </p>

      <div style={{ marginTop: 10 }}>
        <div className="pc-k">Needs parking</div>
        <Hours availability={row.availability} empty="Any days — they left this open" />
      </div>

      <div className="pc-kv">
        <PhotoIdCell row={row} onDoc={onDoc} />
        <Cell k="Email"><a href={`mailto:${row.email}`}>{row.email}</a></Cell>
        <Cell k="Phone"><a href={`tel:${row.phone.replace(/[^\d+]/g, '')}`}>{row.phone}</a></Cell>
        <Cell k="Vehicle">{row.vehicleMake} {row.vehicleModel}</Cell>
        <Cell k="Plate"><span className="pc-plate">{row.plate}</span></Cell>
        {row.note && <Cell k="Note">{row.note}</Cell>}
      </div>

      {/* The other half of the pairing. Everything needed to approve it is here,
          including whether the two weeks actually line up. */}
      {listing && (
        <div className="pc-pair">
          <div className="pc-k">Permit holder — lot {listing.lot}, ${listing.price} per month, {payLabel(listing)}</div>
          <div className="pc-v" style={{ marginTop: 4 }}>
            {listing.name} · <a href={`mailto:${listing.email}`}>{listing.email}</a> · {listing.phone}
          </div>
          <p className="pc-small" style={{ margin: '6px 0 0' }}>
            Offers {shortSummary(listing.availability)}
            {listing.status !== 'approved' && ' — not currently on the board'}
          </p>
          {overlap && (
            <p className="pc-small" style={{ margin: '4px 0 0' }}>
              {overlap.wanted === 0
                ? 'They did not name any days, so anything this spot offers works.'
                : overlap.both.length === 0
                  ? 'No overlap — the days they want are not days this permit is free.'
                  : overlap.missing.length === 0
                    ? `Covers every day they asked for (${overlap.both.map(dayShort).join(', ')}).`
                    : `Covers ${overlap.both.map(dayShort).join(', ')}; not ${overlap.missing.map(dayShort).join(', ')}.`}
            </p>
          )}
        </div>
      )}

      <div className="pc-actions">
        {!listing && approved.length > 0 && (
          <select
            className="pc-select" style={{ width: 'auto' }} value=""
            onChange={e => e.target.value && onMatch(row.id, e.target.value)} aria-label="Pair with a listing"
          >
            <option value="">Pair with a listing…</option>
            {approved.map(l => <option key={l.id} value={l.id}>Lot {l.lot} · ${l.price} · {l.name}</option>)}
          </select>
        )}
        {row.status !== 'connected' && (
          <button type="button" className="pc-btn sm ok" disabled={busy || !listing} onClick={() => onStatus(row.id, 'connected')}>
            {busy ? <SpinnerIcon size={13} /> : null} Approve pairing and introduce
          </button>
        )}
        {row.status !== 'closed' && (
          <button type="button" className="pc-btn sm" disabled={busy} onClick={() => onStatus(row.id, 'closed')}>Close</button>
        )}
        {row.status !== 'new' && (
          <button type="button" className="pc-btn sm" disabled={busy} onClick={() => onStatus(row.id, 'new')}>Reopen</button>
        )}
        <span className="pc-spacer" />
        <button type="button" className="pc-btn sm" disabled={busy} onClick={() => onDelete('requests', row.id)}>Delete</button>
      </div>
    </div>
  );
}

function Login({ onToken }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      onToken(await login(pw));
    } catch (e2) {
      setErr(e2.message || 'Login failed.');
      setBusy(false);
    }
  };

  return (
    <div className="pc-root pc-login">
      <style>{CSS}</style>
      <form className="pc-panel" onSubmit={submit}>
        <div className="pc-brand" style={{ marginBottom: 10 }}>
          <MarkIcon size={20} className="pc-mark" />
          UMaps <span className="pc-brand-sub">ParkConnect</span>
        </div>
        <h1 className="pc-h1">Moderation</h1>
        <p className="pc-small" style={{ marginBottom: 14 }}>
          Listings and parker signups, including the documents people attached.
        </p>
        <input
          className="pc-input" type="password" autoComplete="current-password"
          placeholder="Admin password" value={pw} onChange={e => { setPw(e.target.value); setErr(''); }}
        />
        {err && <div style={{ marginTop: 10 }}><div className="pc-note bad"><CloseIcon size={15} />{err}</div></div>}
        <button type="submit" className="pc-btn primary block" disabled={busy || !pw} style={{ marginTop: 12 }}>
          {busy ? <><SpinnerIcon size={15} /> Checking…</> : 'Log in'}
        </button>
      </form>
    </div>
  );
}

export default function ParkConnectAdmin() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('pending');
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);

  const saveToken = useCallback((t) => {
    setToken(t);
    try { t ? sessionStorage.setItem(TOKEN_KEY, t) : sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setErr('');
    try {
      setData(await fetchAdminData(token, 500));
    } catch (e) {
      if (e.status === 401) { saveToken(''); setData(null); setErr('Session expired — log in again.'); }
      else setErr(e.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [token, saveToken]);

  useEffect(() => { load(); }, [load]);

  // Every decision reloads rather than patching state locally: approving is what
  // puts a listing in front of students, so the table should show what the store
  // actually holds afterwards.
  const act = useCallback(async (id, op, body) => {
    setBusyId(id); setErr('');
    try {
      await post(op, { id, ...body }, token);
      await load();
    } catch (e) {
      if (e.status === 401) { saveToken(''); setData(null); setErr('Session expired — log in again.'); }
      else setErr(e.message || 'That did not go through.');
    } finally {
      setBusyId('');
    }
  }, [token, load, saveToken]);

  const decideListing = useCallback((id, status) => act(id, 'listing-status', { status }), [act]);
  const setRequest = useCallback((id, status) => act(id, 'request-status', { status }), [act]);
  const matchRequest = useCallback((id, matchedListingId) => act(id, 'request-status', { status: 'new', matchedListingId }), [act]);

  const removeRow = useCallback((collection, id) => {
    const what = collection === 'listings' ? 'listing' : 'signup';
    // Deleting takes the person's contact details with it — there is no undo and
    // no history, so it asks first.
    if (!window.confirm(`Delete this ${what} for good? Their details go with it.`)) return;
    act(id, 'delete', { collection });
  }, [act]);

  const openDoc = useCallback(async (id, which, label) => {
    setErr('');
    try {
      const res = await post('file', { id, which }, token);
      // Only a file our own upload endpoint could have produced is framed here.
      // The submission is public, so a row posted straight to the API can name
      // any URL at all — and an arbitrary page inside this modal is a login
      // form that looks like part of the moderation screen.
      const raw = res.dataUrl || res.url;
      const src = storedDocUrl(raw);
      if (!src) {
        setErr(`That document is not a UMaps upload, so it is not shown here: ${String(raw || '').slice(0, 200)}`);
        return;
      }
      setDoc({ src, name: res.name, mime: res.mime, label });
    } catch (e) {
      setErr(e.message || 'That document could not be opened.');
    }
  }, [token]);

  const listings = useMemo(() => data?.listings || [], [data]);
  const requests = useMemo(() => data?.requests || [], [data]);

  const counts = useMemo(() => ({
    pending: listings.filter(l => l.status === 'pending').length,
    live: listings.filter(l => l.status === 'approved').length,
    archive: listings.filter(l => l.status === 'rejected').length,
    requests: requests.filter(r => r.status === 'new').length,
  }), [listings, requests]);

  if (!token) return <Login onToken={saveToken} />;

  const TABS = [
    { id: 'pending', label: 'Waiting on you', n: counts.pending },
    { id: 'requests', label: 'Parker signups', n: counts.requests },
    { id: 'live', label: 'On the board', n: counts.live },
    { id: 'archive', label: 'Rejected', n: counts.archive },
  ];

  const rows = tab === 'pending' ? listings.filter(l => l.status === 'pending')
    : tab === 'live' ? listings.filter(l => l.status === 'approved')
    : tab === 'archive' ? listings.filter(l => l.status === 'rejected')
    : requests;

  return (
    <div className="pc-root">
      <style>{CSS}</style>
      <header className="pc-head">
        <div className="pc-wrap wide pc-head-in">
          <a className="pc-brand" href="/parkconnect">
            <MarkIcon size={22} className="pc-mark" />
            UMaps <span className="pc-brand-sub">ParkConnect</span>
          </a>
          <div className="pc-head-actions">
            <a className="pc-btn sm" href="/parkconnect">Public board</a>
            <button type="button" className="pc-btn sm" onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button type="button" className="pc-btn sm" onClick={() => { saveToken(''); setData(null); }}>Log out</button>
          </div>
        </div>
      </header>

      <main className="pc-wrap wide pc-main">
        <h1 className="pc-h1">Moderation</h1>
        <p className="pc-small" style={{ marginBottom: 16 }}>
          {API_BASE ? 'Local dev — writing to data/parkconnect/store.json' : 'Live'}
        </p>

        {err && <div className="pc-note bad" style={{ marginBottom: 14 }}><CloseIcon size={15} />{err}</div>}

        <div className="pc-tabs">
          {TABS.map(t => (
            <button type="button" key={t.id} className={`pc-tab ${tab === t.id ? 'on' : ''}`} onClick={() => setTab(t.id)}>
              {t.label} <span className="pc-tab-n">{t.n}</span>
            </button>
          ))}
        </div>

        {loading && !data && (
          <div className="pc-empty"><SpinnerIcon size={22} /><p className="pc-small" style={{ marginTop: 10 }}>Loading…</p></div>
        )}

        {!loading && rows.length === 0 && (
          <div className="pc-empty">
            <h2 className="pc-h2">Nothing here</h2>
            <p className="pc-small">
              {tab === 'pending' ? 'No listings are waiting on a decision.'
                : tab === 'requests' ? 'No one has signed up as a parker yet.'
                : tab === 'live' ? 'The board is empty — approve a listing and it appears here.'
                : 'Nothing has been rejected.'}
            </p>
          </div>
        )}

        <div className="pc-rows">
          {tab === 'requests'
            ? rows.map(row => (
              <RequestRow
                key={row.id} row={row} listings={listings} busy={busyId === row.id}
                onStatus={setRequest} onMatch={matchRequest} onDelete={removeRow} onDoc={openDoc}
              />
            ))
            : rows.map(row => (
              <ListingRow
                key={row.id} row={row} busy={busyId === row.id}
                onDecide={decideListing} onDelete={removeRow} onDoc={openDoc}
              />
            ))}
        </div>
      </main>

      {doc && <DocModal doc={doc} onClose={() => setDoc(null)} />}
    </div>
  );
}
